import { CompanyAdminLayout } from "../../components/CompanyAdmin/CompanyAdminLayout";
import { useCallback, useEffect, useRef, useState } from "react";
import TextInput from "../../components/UI/TextInput";
import ToggleableButton from "../../components/UI/ToggleagleButton";
import { businessClientUseCase } from "../../../di/container";
import { businessClientApiService } from "../../../infrastructure/api/company/business-client-api-service";
import { useParams, useNavigate } from "react-router-dom";
import Table from "../../components/UI/Table";
import type { BusinessClient, ManagedClient, CreateManagedClientRequest } from "../../../domain/types/business-client";
import { Plus, X } from "lucide-react";
import { Button } from "../../components/UI/Button";

type TabType = "registered" | "managed";

const BusinessClientManagement: React.FC = () => {
  const { companyId } = useParams();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<TabType>("managed");
  const [searchKeyword, setSearchKeyword] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [isResetButtonEnabled, setIsResetButtonEnabled] = useState(false);

  // 가입 기업 (기존)
  const [clients, setClients] = useState<BusinessClient[]>([]);
  const [clientTotal, setClientTotal] = useState(0);
  const [clientPage, setClientPage] = useState(0);
  const [clientHasMore, setClientHasMore] = useState(true);

  // 등록 거래처 (신규)
  const [managed, setManaged] = useState<ManagedClient[]>([]);
  const [managedTotal, setManagedTotal] = useState(0);
  const [managedPage, setManagedPage] = useState(0);
  const [managedHasMore, setManagedHasMore] = useState(true);

  // 등록 모달
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState<CreateManagedClientRequest>({ businessName: "" });

  const isInitiallyLoaded = useRef(false);
  const isLoadingMore = useRef(false);

  // 가입 기업 로드
  const loadClients = useCallback(
    async (page: number, keyword?: string, reset?: boolean) => {
      if (!companyId) return;
      try {
        const result = await businessClientUseCase.getBusinessClients(
          Number(companyId), keyword || undefined, page, 20,
        );
        setClientTotal(result.totalElements);
        setClientHasMore(result.next !== null);
        if (reset || page === 0) setClients(result.items);
        else setClients((prev) => [...prev, ...result.items]);
      } catch (error) {
        console.error("기업고객 목록 조회 실패:", error);
      }
    },
    [companyId],
  );

  // 등록 거래처 로드
  const loadManaged = useCallback(
    async (page: number, keyword?: string, reset?: boolean) => {
      if (!companyId) return;
      try {
        const response = await businessClientApiService.getManagedClients(
          Number(companyId), keyword || undefined, page, 20,
        );
        const data = response.data;
        if (data) {
          setManagedTotal(data.totalElements);
          setManagedHasMore(data.next !== null);
          if (reset || page === 0) setManaged(data.items);
          else setManaged((prev) => [...prev, ...data.items]);
        }
      } catch (error) {
        console.error("거래처 목록 조회 실패:", error);
      }
    },
    [companyId],
  );

  useEffect(() => {
    if (activeTab === "registered") {
      loadClients(0, searchKeyword, true);
    } else {
      loadManaged(0, searchKeyword, true);
    }
  }, [companyId, activeTab, searchKeyword, loadClients, loadManaged]);

  useEffect(() => {
    setIsResetButtonEnabled(searchInput !== "");
  }, [searchInput]);

  const handleSearch = () => {
    setSearchKeyword(searchInput);
    setClientPage(0);
    setManagedPage(0);
  };

  const handleReset = () => {
    setSearchInput("");
    setSearchKeyword("");
    setClientPage(0);
    setManagedPage(0);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") handleSearch();
  };

  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    setSearchInput("");
    setSearchKeyword("");
    setClientPage(0);
    setManagedPage(0);
  };

  const handleCreate = async () => {
    if (!companyId || !createForm.businessName.trim()) return;
    try {
      await businessClientApiService.createManagedClient(Number(companyId), createForm);
      setShowCreateModal(false);
      setCreateForm({ businessName: "" });
      loadManaged(0, searchKeyword, true);
    } catch (error) {
      console.error("거래처 등록 실패:", error);
      alert("거래처 등록에 실패했습니다.");
    }
  };

  const handleDeleteManaged = async (id: number) => {
    if (!companyId) return;
    if (!window.confirm("거래처를 삭제하시겠습니까?")) return;
    try {
      await businessClientApiService.deleteManagedClient(Number(companyId), id);
      loadManaged(0, searchKeyword, true);
    } catch (error) {
      console.error("거래처 삭제 실패:", error);
      alert("거래처 삭제에 실패했습니다.");
    }
  };

  // 무한 스크롤 (IntersectionObserver)
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const canLoadMore = (activeTab === "registered" ? clients.length > 0 && clientHasMore : managed.length > 0 && managedHasMore);

  useEffect(() => {
    if (!loadMoreRef.current || !canLoadMore || isLoadingMore.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0].isIntersecting || isLoadingMore.current) return;
        if (activeTab === "registered" && clientHasMore) {
          isLoadingMore.current = true;
          const next = clientPage + 1;
          setClientPage(next);
          loadClients(next, searchKeyword).finally(() => { isLoadingMore.current = false; });
        } else if (activeTab === "managed" && managedHasMore) {
          isLoadingMore.current = true;
          const next = managedPage + 1;
          setManagedPage(next);
          loadManaged(next, searchKeyword).finally(() => { isLoadingMore.current = false; });
        }
      },
      { threshold: 0.1 },
    );
    observer.observe(loadMoreRef.current);
    return () => observer.disconnect();
  }, [canLoadMore, activeTab, clientPage, clientHasMore, managedPage, managedHasMore, loadClients, loadManaged, searchKeyword]);

  const formatDate = (dateStr: string) =>
    new Date(dateStr)
      .toLocaleDateString("ko-KR", { year: "numeric", month: "2-digit", day: "2-digit" })
      .replace(/-/g, ". ");

  const tabClass = (tab: TabType) =>
    `px-4 py-2 text-[13px] font-medium cursor-pointer border-b-2 ${
      activeTab === tab
        ? "border-[#4285F4] text-[#4285F4]"
        : "border-transparent text-[#888] hover:text-[#555]"
    }`;

  return (
    <div className="relative">
      <CompanyAdminLayout>
        <div className="p-5">
          <div className="mb-[28px] flex items-center justify-between">
            <div className="flex flex-col items-start gap-[0.5rem]">
              <h2 className="text-[1.375rem] font-semibold">기업 관리</h2>
              <span className="text-[1rem] font-normal">
                {activeTab === "managed"
                  ? `총 ${managedTotal}개의 등록 거래처가 있습니다.`
                  : `총 ${clientTotal}개의 가입 기업이 있습니다.`}
              </span>
            </div>
            {activeTab === "managed" && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="flex items-center gap-1 px-4 py-2 bg-[#222] text-white text-[13px] rounded-[6px] hover:bg-[#333]"
              >
                <Plus size={16} />
                거래처 등록
              </button>
            )}
          </div>

          {/* 탭 */}
          <div className="flex gap-0 border-b border-gray-300 mb-5">
            <div
              className={tabClass("managed")}
              onClick={() => handleTabChange("managed")}
            >
              등록 거래처
            </div>
            <div
              className={tabClass("registered")}
              onClick={() => handleTabChange("registered")}
            >
              가입 기업
            </div>
          </div>

          <div className="text-[12px]">
            {/* 검색 영역 */}
            <div
              className="mb-[1.25rem] p-[1.25rem]
              border border-[#d9d9d9] rounded-[0.625rem] bg-white
              flex flex-col md:flex-row gap-[0.75rem] justify-stretch"
            >
              <TextInput
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={handleKeyDown}
                className="w-full"
                placeholder={
                  activeTab === "registered"
                    ? "업체명, 이름, 이메일로 검색"
                    : "업체명, 사업자번호, 연락처로 검색"
                }
              />
              <ToggleableButton
                isEnabled={true}
                className="min-w-[85px] h-12"
                onClick={handleSearch}
              >
                검색
              </ToggleableButton>
              <ToggleableButton
                isEnabled={isResetButtonEnabled}
                className="min-w-[85px] h-12"
                onClick={handleReset}
              >
                초기화
              </ToggleableButton>
            </div>

            {/* 가입 기업 테이블 */}
            {activeTab === "registered" && (
              <>
                <Table
                  columnWidths={[
                    "60px",
                    "150px",
                    "150px",
                    "140px",
                    "140px",
                    "180px",
                    "120px",
                  ]}
                  tabledata={{
                    head: [
                      <>No.</>,
                      <>기업명</>,
                      <>사업자번호</>,
                      <>기업 연락처</>,
                      <>담당자명</>,
                      <>담당자 이메일</>,
                      <>가입일</>,
                    ],
                    body: clients.map((client, index) => [
                      <>{index + 1}</>,
                      <span
                        role="button"
                        className="hover:underline text-[#4285F4] underline"
                        onClick={() =>
                          navigate(
                            `/company/${companyId}/business-clients/${client.userId}`,
                          )
                        }
                      >
                        {client.businessName || "-"}
                      </span>,
                      <>{client.businessNumber || "-"}</>,
                      <>{client.businessPhone || "-"}</>,
                      <>{client.name || "-"}</>,
                      <>{client.email || "-"}</>,
                      <>{formatDate(client.createdAt)}</>,
                    ]),
                  }}
                />
                {clients.length === 0 && (
                  <div className="text-center py-10 text-[#888] text-[14px]">
                    가입된 기업이 없습니다.
                  </div>
                )}
              </>
            )}

            {/* 등록 거래처 테이블 */}
            {activeTab === "managed" && (
              <>
                <Table
                  columnWidths={[
                    "60px",
                    "150px",
                    "150px",
                    "140px",
                    "100px",
                    "120px",
                    "100px",
                  ]}
                  tabledata={{
                    head: [
                      <>No.</>,
                      <>업체명</>,
                      <>사업자번호</>,
                      <>기업 연락처</>,
                      <>회원 연결</>,
                      <>등록일</>,
                      <></>,
                    ],
                    body: managed.map((item, index) => [
                      <>{index + 1}</>,
                      <span
                        role="button"
                        className="hover:underline text-[#4285F4] underline"
                        onClick={() =>
                          navigate(
                            `/company/${companyId}/business-clients/managed/${item.id}`,
                          )
                        }
                      >
                        {item.businessName || "-"}
                      </span>,
                      <>{item.businessNumber || "-"}</>,
                      <>{item.businessPhone || "-"}</>,
                      <span
                        className={`inline-block px-2 py-0.5 rounded text-[11px] ${
                          item.userId
                            ? "bg-[#E8EFFF] text-[#4285F4]"
                            : "bg-[#f5f5f5] text-[#888]"
                        }`}
                      >
                        {item.userId ? "연결됨" : "미연결"}
                      </span>,
                      <>{formatDate(item.createdAt)}</>,
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteManaged(item.id);
                        }}
                        variant="danger"
                      >
                        삭제
                      </Button>,
                    ]),
                  }}
                />
                {managed.length === 0 && (
                  <div className="text-center py-10 text-[#888] text-[14px]">
                    등록된 거래처가 없습니다. 상단의 "거래처 등록" 버튼을 눌러
                    추가하세요.
                  </div>
                )}
              </>
            )}
          <div ref={loadMoreRef} className="h-4" />
          </div>
        </div>
      </CompanyAdminLayout>

      {/* 거래처 등록 모달 */}
      {showCreateModal && (
        <div className="z-[5000] fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-[12px] w-[480px] max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-[#eee]">
              <h3 className="text-[16px] font-bold">거래처 등록</h3>
              <button onClick={() => setShowCreateModal(false)}>
                <X size={20} className="text-[#888]" />
              </button>
            </div>
            <div className="p-5 flex flex-col text-start gap-3">
              <FormField
                label="업체명 *"
                value={createForm.businessName}
                onChange={(v) =>
                  setCreateForm({ ...createForm, businessName: v })
                }
              />
              <FormField
                label="사업자번호"
                value={createForm.businessNumber || ""}
                onChange={(v) =>
                  setCreateForm({ ...createForm, businessNumber: v })
                }
              />
              <FormField
                label="기업 연락처"
                value={createForm.businessPhone || ""}
                onChange={(v) =>
                  setCreateForm({ ...createForm, businessPhone: v })
                }
              />
              <FormField
                label="업태"
                value={createForm.businessType || ""}
                onChange={(v) =>
                  setCreateForm({ ...createForm, businessType: v })
                }
              />
              <FormField
                label="업종"
                value={createForm.businessCategory || ""}
                onChange={(v) =>
                  setCreateForm({ ...createForm, businessCategory: v })
                }
              />
              <FormField
                label="우편번호"
                value={createForm.postcode || ""}
                onChange={(v) => setCreateForm({ ...createForm, postcode: v })}
              />
              <FormField
                label="주소"
                value={createForm.address || ""}
                onChange={(v) => setCreateForm({ ...createForm, address: v })}
              />
              <FormField
                label="상세주소"
                value={createForm.addressDetail || ""}
                onChange={(v) =>
                  setCreateForm({ ...createForm, addressDetail: v })
                }
              />
              <FormField
                label="세금계산서 담당자"
                value={createForm.taxManagerName || ""}
                onChange={(v) =>
                  setCreateForm({ ...createForm, taxManagerName: v })
                }
              />
              <FormField
                label="담당자 연락처"
                value={createForm.taxManagerPhone || ""}
                onChange={(v) =>
                  setCreateForm({ ...createForm, taxManagerPhone: v })
                }
              />
              <FormField
                label="세금계산서 이메일"
                value={createForm.taxEmail || ""}
                onChange={(v) => setCreateForm({ ...createForm, taxEmail: v })}
              />
            </div>
            <div className="flex gap-2 p-5 border-t border-[#eee]">
              <Button
                onClick={() => setShowCreateModal(false)}
                variant="outline"
                className="w-full"
              >
                취소
              </Button>
              <ToggleableButton
                className="w-full"
                onClick={handleCreate}
                isEnabled={createForm.businessName.trim().length > 0}
              >
                등록
              </ToggleableButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const FormField: React.FC<{
  label: string;
  value: string;
  onChange: (value: string) => void;
}> = ({ label, value, onChange }) => (
  <div className="flex flex-col gap-1">
    <label className="text-[12px] font-medium text-[#555]">{label}</label>
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="border border-[#e0e0e0] rounded-[8px] px-3 py-2 text-[13px] focus:border-[#4285F4] focus:outline-none"
    />
  </div>
);

export default BusinessClientManagement;
